import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  CircularProgress,
  Link,
  alpha,
  Chip,
  Button,
  Switch,
  Tooltip,
  Fade,
  Zoom,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { getBookieLogoUrl, getSportsIcon } from "../../constants";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Link as RouterLink } from "react-router-dom";

import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CalculateIcon from "@mui/icons-material/Calculate";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import useDraggable from "../../hooks/useDraggable";

const GroupedBetModal = ({
  open,
  onClose,
  groupedBetData,
  loading,
  error,
  bet,
  fullBet,
  league,
  market,
  event,
  event_id,
  evMode,
  onRefetchGroupedBet,
  excludedBookies = [],
  parentIncludeEstimates = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // Draggable functionality for desktop
  const { handleMouseDown, getDialogStyle, getDragHandleStyle, isDragging } =
    useDraggable(isDesktop, open, "grouped_bet");

  const [sortConfig, setSortConfig] = useState({
    key: bet,
    direction: "desc",
  });
  const [fairOddsPerSelection, setFairOddsPerSelection] = useState({});
  const [allBets, setAllBets] = useState([]);
  const [includeEstimatesFo, setIncludeEstimatesFo] = useState(parentIncludeEstimates);

  // Reset sort config when modal closes or bet changes
  useEffect(() => {
    if (bet) {
      setSortConfig({
        key: bet,
        direction: "desc",
      });
    }
  }, [bet, open]);

  // Reset include estimates toggle to parent value when modal opens
  useEffect(() => {
    if (open) {
      setIncludeEstimatesFo(parentIncludeEstimates);
    }
  }, [open, parentIncludeEstimates]);

  // Update fair odds values when groupedBetData changes
  useEffect(() => {
    if (groupedBetData?.bets && evMode) {
      const fairOddsMap = {};

      // Extract unique selections
      const selectionsSet = new Set();
      groupedBetData.bets.forEach((bookieBet) => {
        bookieBet.selections.forEach((selection) => {
          selectionsSet.add(selection.bet);
        });
      });

      // For each unique selection, get the fair odds from the first bookie
      Array.from(selectionsSet).forEach((selectionName) => {
        for (const bookieBet of groupedBetData.bets) {
          const selection = bookieBet.selections.find(
            (s) => s.bet === selectionName
          );
          if (selection && selection.fairOdds) {
            fairOddsMap[selectionName] = selection.fairOdds;
            break;
          }
        }
      });

      setFairOddsPerSelection(fairOddsMap);

      // Store all bets to keep track of excluded bookies
      setAllBets((prevBets) => {
        // Merge current bets with previous bets that are now excluded
        const currentBookies = new Set(
          groupedBetData.bets.map((bet) => bet.bookie)
        );
        const excludedPrevBets = prevBets.filter(
          (bet) =>
            excludedBookies.includes(bet.bookie) &&
            !currentBookies.has(bet.bookie)
        );

        return [...groupedBetData.bets, ...excludedPrevBets];
      });
    }
  }, [groupedBetData, evMode, excludedBookies]);

  const sortedBets = useMemo(() => {
    // Use allBets instead of groupedBetData.bets to include excluded bookies
    const bets = allBets.length > 0 ? allBets : groupedBetData?.bets || [];

    return [...bets].sort((a, b) => {
      if (evMode) {
        const aSelection = a.selections.find((s) => s.bet === sortConfig.key);
        const bSelection = b.selections.find((s) => s.bet === sortConfig.key);

        // If no matching selection found, put it at the end
        if (!aSelection) return 1;
        if (!bSelection) return -1;

        // Sort by rate
        const multiplier = sortConfig.direction === "asc" ? 1 : -1;
        return (aSelection.rate - bSelection.rate) * multiplier;
      } else {
        // For non-EV mode, sort by rate directly
        const multiplier = sortConfig.direction === "asc" ? 1 : -1;
        return (a.selections[0].rate - b.selections[0].rate) * multiplier;
      }
    });
  }, [groupedBetData?.bets, allBets, sortConfig, evMode]);

  // Get unique bet types for columns
  const uniqueBets = useMemo(() => {
    if (!evMode) {
      // In non-EV mode, we only have one bet type
      return [market];
    }
    const betSet = new Set();
    sortedBets.forEach((bet) => {
      bet.selections.forEach((selection) => {
        betSet.add(selection.bet);
      });
    });
    return Array.from(betSet);
  }, [sortedBets, evMode, market]);

  const handleSort = (betType) => {
    setSortConfig((prevConfig) => ({
      key: betType,
      direction:
        prevConfig.key === betType && prevConfig.direction === "desc"
          ? "asc"
          : "desc",
    }));
  };

  const handleToggleBookmaker = (bookieName) => {
    if (onRefetchGroupedBet && evMode) {
      const newExcludedBookies = excludedBookies.includes(bookieName)
        ? excludedBookies.filter((b) => b !== bookieName)
        : [...excludedBookies, bookieName];

      onRefetchGroupedBet(newExcludedBookies, includeEstimatesFo);
    }
  };

  const handleToggleIncludeEstimates = (event) => {
    const newValue = event.target.checked;
    setIncludeEstimatesFo(newValue);

    if (onRefetchGroupedBet && evMode) {
      onRefetchGroupedBet(excludedBookies, newValue);
    }
  };

  if (!groupedBetData) return null;

  if (loading) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: isDark
              ? "linear-gradient(180deg, #1a1a1a 0%, #121212 100%)"
              : "linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)",
          },
        }}
      >
        <Box sx={{
          p: 6,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}>
          <CircularProgress
            size={48}
            thickness={3}
            sx={{
              color: theme.palette.primary.main,
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              opacity: 0.7,
            }}
          >
            Loading odds data...
          </Typography>
        </Box>
      </Dialog>
    );
  }

  if (error || !groupedBetData?.bets) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 4,
          },
        }}
      >
        <DialogContent>
          <Typography color="error" sx={{ textAlign: "center" }}>
            {error ? "Error loading bet data" : "No bet data available"}
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  // Bookmaker row component
  const BookieRow = ({ betItem, index }) => {
    const [imageError, setImageError] = useState(false);
    const isExcluded = excludedBookies.includes(betItem.bookie);
    const rowClickable = betItem.selections[0]?.source;

    return (
      <Fade in timeout={100 + index * 30}>
        <Box
          onClick={() => rowClickable && window.open(betItem.selections[0].source)}
          sx={{
            display: "grid",
            gridTemplateColumns: `minmax(160px, 180px) ${uniqueBets.map(() => "minmax(90px, 1fr)").join(" ")} ${evMode ? "36px" : "0px"}`,
            gap: 0.5,
            py: 0.5,
            px: 1,
            borderRadius: 1.5,
            cursor: rowClickable ? "pointer" : "default",
            opacity: isExcluded ? 0.45 : 1,
            position: "relative",
            transition: "all 0.15s ease",
            background: isDark ? alpha("#ffffff", 0.02) : alpha("#000000", 0.01),
            border: `1px solid ${isDark ? alpha("#ffffff", 0.06) : alpha("#000000", 0.06)}`,
            "&:hover": rowClickable ? {
              background: isDark
                ? alpha("#ffffff", 0.06)
                : alpha("#000000", 0.04),
              borderColor: alpha(theme.palette.primary.main, 0.4),
            } : {},
          }}
        >
          {/* Bookmaker info */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {imageError ? (
              <AccountBalanceWalletIcon
                sx={{ fontSize: 20, color: theme.palette.primary.main }}
              />
            ) : (
              <img
                src={getBookieLogoUrl(betItem.bookie)}
                alt={betItem.bookie}
                onError={() => setImageError(true)}
                style={{
                  width: 140,
                  height: 32,
                  objectFit: "contain",
                }}
              />
            )}
            {rowClickable && (
              <OpenInNewIcon
                sx={{
                  fontSize: "0.7rem",
                  color: theme.palette.primary.main,
                  opacity: 0.5,
                }}
              />
            )}
          </Box>

          {/* Odds cells */}
          {uniqueBets.map((betType) => {
            const selection = evMode
              ? betItem.selections.find((s) => s.bet === betType)
              : betItem.selections[0];

            if (!selection) {
              return (
                <Box key={betType} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary, opacity: 0.4 }}>
                    —
                  </Typography>
                </Box>
              );
            }

            const isHighlighted = evMode && selection.bet === bet;

            return (
              <Box
                key={betType}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  py: 0.25,
                  px: 0.75,
                  borderRadius: 1,
                  background: isHighlighted
                    ? alpha(theme.palette.primary.main, isDark ? 0.2 : 0.1)
                    : "transparent",
                }}
              >
                <Typography
                  sx={{
                    fontSize: isHighlighted ? "0.85rem" : "0.8rem",
                    fontWeight: isHighlighted ? 700 : 500,
                    color: isHighlighted
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {selection.rate?.toFixed(2)}
                </Typography>
                {evMode && selection.deviggedOdds && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.7rem",
                      color: theme.palette.text.secondary,
                      opacity: 0.6,
                    }}
                  >
                    ({selection.deviggedOdds?.toFixed(2)})
                  </Typography>
                )}
                {selection?.oddsEstimated === 1 && (
                  <Tooltip title="Estimated odds" arrow placement="top">
                    <CalculateIcon
                      sx={{
                        fontSize: "0.75rem",
                        color: theme.palette.warning?.main || "#f59e0b",
                        opacity: 0.8,
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          })}

          {/* Exclude toggle */}
          {evMode && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Tooltip title={isExcluded ? "Include" : "Exclude"} arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleBookmaker(betItem.bookie);
                  }}
                  sx={{
                    p: 0.25,
                    color: isExcluded
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                    opacity: isExcluded ? 1 : 0.5,
                    transition: "all 0.15s ease",
                    "&:hover": {
                      opacity: 1,
                      background: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  {isExcluded ? (
                    <AddCircleOutlineIcon sx={{ fontSize: "1rem" }} />
                  ) : (
                    <RemoveCircleOutlineIcon sx={{ fontSize: "1rem" }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Fade>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Zoom}
      transitionDuration={250}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: isDark
            ? "linear-gradient(180deg, #1a1a1a 0%, #121212 100%)"
            : "linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)",
          boxShadow: isDark
            ? "0 25px 50px -12px rgba(0, 0, 0, 0.5)"
            : "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
          border: `1px solid ${isDark ? alpha("#ffffff", 0.08) : alpha("#000000", 0.08)}`,
          overflow: "hidden",
          ...getDialogStyle(),
          transition: isDragging ? "none" : "transform 0.1s ease-out",
        },
      }}
    >
      {/* Header gradient accent */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.4)} 100%)`,
        }}
      />

      <DialogContent sx={{ p: 0 }}>
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 10,
            color: theme.palette.text.secondary,
            background: isDark ? alpha("#ffffff", 0.05) : alpha("#000000", 0.05),
            "&:hover": {
              background: isDark ? alpha("#ffffff", 0.1) : alpha("#000000", 0.1),
            },
          }}
        >
          <CloseIcon sx={{ fontSize: "1.1rem" }} />
        </IconButton>

        {/* Draggable header area */}
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            p: 2,
            pb: 1.5,
            ...getDragHandleStyle(),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap", pr: 5 }}>
            {isDesktop && (
              <DragIndicatorIcon
                sx={{
                  fontSize: 18,
                  color: theme.palette.text.disabled,
                  opacity: 0.6,
                }}
              />
            )}
            {getSportsIcon(league?.split(" ")[0])
              ? React.cloneElement(getSportsIcon(league?.split(" ")[0]), {
                  sx: {
                    color: theme.palette.primary.main,
                    fontSize: "1rem",
                  },
                })
              : null}
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: theme.palette.text.secondary,
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {league}
            </Typography>
            <Chip
              label={market}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                background: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                "& .MuiChip-label": { px: 1 },
              }}
            />
            <Button
              component={RouterLink}
              to={`/event/${event_id}?market=${encodeURIComponent(market)}`}
              sx={{
                textTransform: "none",
                color: theme.palette.text.secondary,
                p: 0,
                ml: "auto",
                minWidth: "auto",
                fontSize: "0.75rem",
                fontWeight: 400,
                "&:hover": {
                  backgroundColor: "transparent",
                  color: theme.palette.primary.main,
                },
              }}
            >
              {event}
              <OpenInNewIcon sx={{ fontSize: "0.65rem", ml: 0.5, opacity: 0.5 }} />
            </Button>
          </Box>

          <Typography
            sx={{
              fontSize: "1.1rem",
              fontWeight: 700,
              color: theme.palette.text.primary,
              lineHeight: 1.2,
            }}
          >
            {fullBet}
          </Typography>
        </Box>

        {/* EV Mode info panel */}
        {!!evMode && (
          <Box
            sx={{
              mx: 2,
              mb: 1.5,
              p: 2,
              borderRadius: 2.5,
              background: isDark
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha("#000000", 0.3)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            {/* Fair odds display */}
            {Object.keys(fairOddsPerSelection).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <TrendingUpIcon sx={{ fontSize: "1rem", color: theme.palette.primary.main }} />
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Fair Odds
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {uniqueBets.map((selection) => (
                    <Box
                      key={selection}
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        background: selection === bet
                          ? alpha(theme.palette.primary.main, isDark ? 0.25 : 0.15)
                          : isDark ? alpha("#ffffff", 0.05) : alpha("#000000", 0.04),
                        border: selection === bet
                          ? `1px solid ${alpha(theme.palette.primary.main, 0.5)}`
                          : `1px solid ${isDark ? alpha("#ffffff", 0.1) : alpha("#000000", 0.08)}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: theme.palette.text.secondary,
                          mb: 0.25,
                        }}
                      >
                        {selection}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          color: selection === bet
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fairOddsPerSelection[selection]?.toFixed(3) || "N/A"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Settings row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                pt: Object.keys(fairOddsPerSelection).length > 0 ? 2 : 0,
                borderTop: Object.keys(fairOddsPerSelection).length > 0
                  ? `1px solid ${isDark ? alpha("#ffffff", 0.08) : alpha("#000000", 0.08)}`
                  : "none",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch
                  checked={includeEstimatesFo}
                  onChange={handleToggleIncludeEstimates}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: theme.palette.primary.main,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                />
                <Typography sx={{ fontSize: "0.8rem", color: theme.palette.text.secondary }}>
                  Include estimated odds
                </Typography>
              </Box>

              <Link
                href="https://help.bonusbank.com.au/article/393-how-are-fair-odds-calculated"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: "0.75rem",
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  opacity: 0.8,
                  "&:hover": {
                    opacity: 1,
                    textDecoration: "underline",
                  },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: "0.85rem" }} />
                How are fair odds calculated?
              </Link>
            </Box>
          </Box>
        )}

        {/* Column headers */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `minmax(160px, 180px) ${uniqueBets.map(() => "minmax(90px, 1fr)").join(" ")} ${evMode ? "36px" : "0px"}`,
            gap: 0.5,
            mx: 2,
            px: 1,
            py: 1,
            borderTop: `1px solid ${isDark ? alpha("#ffffff", 0.08) : alpha("#000000", 0.08)}`,
            borderBottom: `1px solid ${isDark ? alpha("#ffffff", 0.08) : alpha("#000000", 0.08)}`,
            background: isDark ? alpha("#000000", 0.2) : alpha("#000000", 0.02),
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              color: theme.palette.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              display: "flex",
              alignItems: "center",
            }}
          >
            Bookmaker
          </Typography>
          {uniqueBets.map((betType) => (
            <Box
              key={betType}
              onClick={() => handleSort(betType)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                cursor: "pointer",
                userSelect: "none",
                borderRadius: 1,
                py: 0.5,
                mx: -0.5,
                px: 0.5,
                transition: "background 0.15s ease",
                "&:hover": {
                  background: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: betType === bet ? 700 : 600,
                  color: betType === bet ? theme.palette.primary.main : theme.palette.text.secondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  textAlign: "center",
                }}
              >
                {betType}
              </Typography>
              {sortConfig.key === betType && (
                sortConfig.direction === "desc" ? (
                  <ArrowDownwardIcon sx={{ fontSize: "0.85rem", color: theme.palette.primary.main }} />
                ) : (
                  <ArrowUpwardIcon sx={{ fontSize: "0.85rem", color: theme.palette.primary.main }} />
                )
              )}
            </Box>
          ))}
          {evMode && <Box />}
        </Box>

        {/* Bookmaker rows */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            maxHeight: "60vh",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? alpha("#ffffff", 0.2) : alpha("#000000", 0.2),
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: isDark ? alpha("#ffffff", 0.3) : alpha("#000000", 0.3),
            },
          }}
        >
          {sortedBets.map((betItem, index) => (
            <BookieRow key={betItem.bookie} betItem={betItem} index={index} />
          ))}
        </Box>

        {/* Legend footer */}
        {evMode && (
          <Box
            sx={{
              px: 2,
              py: 1,
              borderTop: `1px solid ${isDark ? alpha("#ffffff", 0.08) : alpha("#000000", 0.08)}`,
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalculateIcon sx={{ fontSize: "0.75rem", color: theme.palette.warning?.main || "#f59e0b" }} />
              <Typography sx={{ fontSize: "0.65rem", color: theme.palette.text.secondary }}>
                Estimated odds
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <RemoveCircleOutlineIcon sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary, opacity: 0.5 }} />
              <Typography sx={{ fontSize: "0.65rem", color: theme.palette.text.secondary }}>
                Exclude from calculation
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GroupedBetModal;
